import { WEBXR_TO_NUE } from "./webxr-nue-basis.js";
import { SCENE_NODE } from "./scene-node-names.js";
import * as THREE from "three";
//#region ../src/ar/ar-scene-hierarchy.ts
/**
* AR scene-graph construction — the shared root of both render paths.
*
* This is the ONE builder for the scene graph, used by live AR
* (`webxr-session.ts`) and by desktop replay (`replay-scene.ts`). It lives in
* its own module precisely because it has two consumers: while it sat inside
* `webxr-session.ts`, the replay path had to import the live-session module —
* and with it the module-level `activeSession` singleton, the XR frame loop and
* the three capture subsystems — to build a scene that never enters a WebXR
* session at all.
*
* The module is pure apart from reading `window.innerWidth/innerHeight` for the
* initial camera aspect.
*/
/**
* AR camera frustum constants — the single source of truth for live AR and
* replay (both build their camera via {@link createSceneHierarchy}).
*
* F2 (2026-07-04 user feedback): far raised 100 → 200 m so objects in the
* reported 100–200 m range are no longer frustum-culled. The far-plane
* distance itself is essentially free at this app's object counts; the real
* constraint is depth precision — far/near = 2×10⁴ is comfortable for a
* 24-bit depth buffer. Revisit the ratio if AR_CAMERA_NEAR ever shrinks.
*
* Note: these apply to WebGL content only. The CSS3D minimap is composited by
* the browser from the camera fov alone — near/far do not clip it (F1 in the
* same feedback doc).
*
* Module-private (no consumer outside this file): the values are pinned
* observably on the constructed camera by the frustum test in
* `ar-scene-hierarchy.test.ts`.
*/
const AR_CAMERA_FOV = 70;
const AR_CAMERA_NEAR = .01;
const AR_CAMERA_FAR = 200;
/**
* Create the scene hierarchy with proper AR/GPS frame separation.
* This is a pure function for testability.
*
* Hierarchy:
*   scene (GPS world frame — NUE: X=North, Y=Up, Z=East)
*   ├── ambientLight
*   ├── directionalLight
*   └── arWorldGroup (local space = NUE; receives alignment matrix)
*       └── basisChangeNode ('webxr-to-nue', constant WEBXR_TO_NUE matrix)
*           └── arpose (Object3D — AR pose; local space = WebXR)
*               └── camera (PerspectiveCamera)
*
* basisChangeNode is a static scene-graph node that holds the WEBXR_TO_NUE
* basis-change matrix permanently (matrixAutoUpdate=false). Moving it here
* instead of composing it in applyAlignmentMatrix() keeps arWorldGroup's
* local space in the **NUE axis convention** (X=North, Y=Up, Z=East), so no
* WebXR↔NUE swizzle is needed for children.
*
* ─────────────────────────────────────────────────────────────────────────
* WHERE DO I ATTACH MY CONTENT? Answer this before reading anything else.
* ─────────────────────────────────────────────────────────────────────────
*
* **Content at a fixed GEOGRAPHIC place (lat/lon), built once → THE SCENE
* ROOT, with raw GPS-world NUE coordinates.** Nothing to pre-multiply, no
* container, nothing per frame. The scene root IS the GPS-world frame, so a
* city mesh, a building outline or any pre-built geographic geometry simply
* goes there, and the lerped alignment on arWorldGroup moves the CAMERA
* through a world that stands still. This is the common case for map-derived
* content and it is the cheap one.
*
* **Content that must stay glued to what SLAM sees, or that is RE-SOLVED from
* GPS repeatedly → under arWorldGroup**, and then the alignment⁻¹ rule below
* applies. Use `createGpsAnchor`, which does the conversion for you.
*
* The trade, so the choice is informed rather than copied: scene-root content
* is geographically truthful but appears to shift against the camera
* passthrough as the fusion refines the alignment (the lerp is what smooths
* that). arWorldGroup content is visually locked to the real world but its
* geographic position drifts, which is why an anchor there re-solves.
*
* CAUTION — two NUE frames: arWorldGroup's local space is the *AR-odometry*
* NUE frame, i.e. the **domain** of the alignment matrix, NOT the GPS-world
* NUE frame of the scene root. Only content authored in AR-odometry
* coordinates (e.g. the camera subtree) may be placed with raw local values.
* GPS-world content (a lat/lon → NUE point) is expressed in the scene-root
* frame and must be pre-multiplied by alignment⁻¹ before being used as a
* local position under arWorldGroup — see createGpsAnchor and the
* alignment-frame bug doc
* (GpsPlusSlamJs_Docs/docs/2026-05-31-gps-anchor-alignment-frame-bug.md).
*
* **That rule is about placing GPS-world content UNDER arWorldGroup. It is not
* a reason to avoid the scene root** — a reading this comment invited twice.
* Two independent readers (one an agent writing an AR plan, one an agent
* reviewing it) concluded from the paragraph above that GPS-world content had
* to live under arWorldGroup behind an inverse-alignment container, and both
* were wrong in a way that would have produced a city pinned to the session's
* arbitrary start pose. The paragraph says what to do IF you put it there; the
* block at the top says where to put it.
*
* - Recording: arpose stays at identity; WebXRManager writes to camera.
* - Replay: arpose receives recorded odomPosition/odomRotation;
*   camera is owned by user controls (OrbitControls / FPS).
*
* @returns Object containing scene, arWorldGroup, arpose, and camera
*/
function createSceneHierarchy() {
	const newScene = new THREE.Scene();
	const newArWorldGroup = new THREE.Group();
	newArWorldGroup.name = "ar-world";
	newScene.add(newArWorldGroup);
	const newBasisChangeNode = new THREE.Group();
	newBasisChangeNode.name = SCENE_NODE.BASIS_CHANGE;
	newBasisChangeNode.matrix.copy(WEBXR_TO_NUE);
	newBasisChangeNode.matrixAutoUpdate = false;
	newArWorldGroup.add(newBasisChangeNode);
	const newArPose = new THREE.Object3D();
	newArPose.name = "ar-pose";
	newBasisChangeNode.add(newArPose);
	const newCamera = new THREE.PerspectiveCamera(AR_CAMERA_FOV, window.innerWidth / window.innerHeight, AR_CAMERA_NEAR, AR_CAMERA_FAR);
	newArPose.add(newCamera);
	const ambientLight = new THREE.AmbientLight(16777215, .5);
	ambientLight.name = SCENE_NODE.AMBIENT_LIGHT;
	newScene.add(ambientLight);
	const directionalLight = new THREE.DirectionalLight(16777215, .8);
	directionalLight.name = SCENE_NODE.SUN_LIGHT;
	directionalLight.position.set(0, 10, 5);
	newScene.add(directionalLight);
	return {
		scene: newScene,
		arWorldGroup: newArWorldGroup,
		arpose: newArPose,
		camera: newCamera
	};
}
//#endregion
export { createSceneHierarchy };
